/**
 * Global formatter utilities
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const isIndianWallClockAfterTwoPm = (): boolean => {
  const ist = dayjs.utc().utcOffset(330);
  const cutoff = ist.clone().hour(14).minute(0).second(0).millisecond(0);
  return ist.isAfter(cutoff);
};

export const toBookingPlanStartUtcIso = (isoOrDate: string | Date): string => {
  const d =
    isoOrDate instanceof Date ? dayjs.utc(isoOrDate) : dayjs.utc(String(isoOrDate).trim());
  if (!d.isValid()) {
    return typeof isoOrDate === "string" ? String(isoOrDate).trim() : "";
  }
  return `${d.format("YYYY-MM-DD")}T18:30:00.000Z`;
};

export const toBookingPlanEndUtcIso = (isoOrDate: string | Date): string => {
  const d =
    isoOrDate instanceof Date ? dayjs.utc(isoOrDate) : dayjs.utc(String(isoOrDate).trim());
  if (!d.isValid()) {
    return typeof isoOrDate === "string" ? String(isoOrDate).trim() : "";
  }
  return `${d.format("YYYY-MM-DD")}T18:29:59.999Z`;
};

/**
 * Converts timestamp to IST (only in production environment)
 * In production: Adds 5 hours 30 minutes to convert UTC to IST
 * In QA/UAT/Dev: Returns timestamp as-is (already in IST)
 * 
 * @param timestamp - The timestamp string or Date object to convert
 * @returns Converted Date object (IST in production, original in other envs)
 */
export const convertTimestampToIST = (
  timestamp: string | Date | null | undefined
): Date | null => {
  if (!timestamp) {
    return null;
  }

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  const isProductionByUrl = apiBaseUrl.includes('services.ridenimbo.com') || 
                           apiBaseUrl.includes('ridenimbo.com');
  
  const isProduction = 
    process.env.REACT_APP_ENV === 'production' ||
    process.env.NODE_ENV === 'production' ||
    isProductionByUrl;
  
  try {
    let date;
    
    if (timestamp instanceof Date) {
      date = dayjs(timestamp);
    } else {
      const timestampStr = String(timestamp);
      
      if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(timestampStr)) {
        date = dayjs(timestampStr, 'MM/DD/YYYY HH:mm:ss', true);
      } else {
        date = dayjs(timestampStr);
      }
    }
    
    if (!date.isValid()) {
      try {
        const fallbackDate = new Date(timestamp);
        if (isNaN(fallbackDate.getTime())) {
          return null;
        }
        date = dayjs(fallbackDate);
      } catch {
        return null;
      }
    }

    if (!isProduction) {
      return date.toDate();
    }

    const convertedDate = date.add(5, 'hour').add(30, 'minute');
    return convertedDate.toDate();
  } catch (error) {
    try {
      return timestamp instanceof Date ? timestamp : new Date(timestamp);
    } catch {
      return null;
    }
  }
};

/** Display date/time with IST rules from `convertTimestampToIST` — e.g. `May 28 2026, 05:23 PM`. */
export const formatTimestampAsDateTime = (
  timestamp: string | Date | null | undefined,
): string => {
  if (!timestamp) return "";
  try {
    const date = convertTimestampToIST(timestamp);
    if (!date) return "";
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${month} ${day} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
  } catch {
    return "";
  }
};

/** Display date only with IST rules from `convertTimestampToIST` — e.g. `May 28, 2026`. */
export const formatTimestampAsDate = (
  timestamp: string | Date | null | undefined,
): string => {
  if (!timestamp) return "";
  const date = convertTimestampToIST(timestamp);
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Formats a mobile number to display format
 * Converts "919912541391" to "+91 9912541391"
 * Handles various input formats:
 * - "919912541391" -> "+91 9912541391"
 * - "9912541391" -> "+91 9912541391"
 * - "+919912541391" -> "+91 9912541391"
 * - "+91 9912541391" -> "+91 9912541391" (already formatted)
 * 
 * @param mobileNumber - The mobile number to format (can be string or number)
 * @param defaultCountryCode - Default country code to use if not present (default: "+91")
 * @returns Formatted mobile number string like "+91 9912541391" or original value if invalid
 */
export const formatMobileNumber = (
  mobileNumber: string | number | null | undefined,
  defaultCountryCode: string = "+91"
): string => {
  // Handle null, undefined, or empty values
  if (!mobileNumber && mobileNumber !== 0) {
    return "";
  }

  // Convert to string and remove all whitespace
  let cleaned = String(mobileNumber).replace(/\s+/g, "");

  // If empty after cleaning, return empty string
  if (!cleaned) {
    return "";
  }

  // If already in correct format (starts with + and has space), return as is
  if (/^\+\d{1,3}\s\d{10}$/.test(cleaned)) {
    return cleaned;
  }

  // Remove leading + if present
  if (cleaned.startsWith("+")) {
    cleaned = cleaned.substring(1);
  }

  // Check if it starts with country code (91 for India)
  let countryCode = defaultCountryCode.replace("+", "");
  let number = cleaned;

  // If number starts with country code, extract it
  if (cleaned.startsWith(countryCode)) {
    number = cleaned.substring(countryCode.length);
    countryCode = defaultCountryCode.replace("+", "");
  } else if (cleaned.length === 10) {
    // If it's a 10-digit number, assume it's the local number
    number = cleaned;
  } else if (cleaned.length > 10) {
    // If longer than 10 digits, try to extract country code
    // For India, country code is 91 (2 digits)
    if (cleaned.startsWith("91") && cleaned.length === 12) {
      countryCode = "91";
      number = cleaned.substring(2);
    } else {
      // Try to extract first 1-3 digits as country code
      // This is a best-effort approach
      const possibleCountryCode = cleaned.substring(0, Math.min(3, cleaned.length - 10));
      if (cleaned.length - possibleCountryCode.length === 10) {
        countryCode = possibleCountryCode;
        number = cleaned.substring(possibleCountryCode.length);
      }
    }
  }

  // Validate that the number part is 10 digits
  if (!/^\d{10}$/.test(number)) {
    // If not valid, return original value
    return String(mobileNumber);
  }

  // Format as "+{countryCode} {number}"
  return `+${countryCode} ${number}`;
};

/**
 * Removes formatting from a mobile number
 * Converts "+91 9912541391" to "919912541391"
 * 
 * @param formattedNumber - The formatted mobile number
 * @returns Unformatted mobile number string
 */
export const unformatMobileNumber = (formattedNumber: string | null | undefined): string => {
  if (!formattedNumber) {
    return "";
  }
  // Remove all non-digit characters
  return formattedNumber.replace(/\D/g, "");
};

/** .NET ticks from 0001-01-01 00:00:00 to 1970-01-01 00:00:00 UTC (1 tick = 100 ns). */
const TICKS_UNIX_EPOCH_OFFSET = 621355968000000000;

/**
 * Extracts .NET ticks from transaction ID (format: prefix_number_ticks).
 * Converts ticks to UTC then to IST (UTC+5:30).
 * Returns time only in 12-hour format with AM/PM (e.g. 4:33:32 PM). No date, no milliseconds.
 */
export const formatTransactionIdAsIST = (transactionId: string | null | undefined): string => {
  if (!transactionId || typeof transactionId !== "string") {
    return "—";
  }
  const parts = transactionId.trim().split("_");
  const lastPart = parts.at(-1);
  if (!lastPart || !/^\d+$/.test(lastPart)) {
    return "—";
  }
  const ticks = Number(lastPart);
  if (Number.isNaN(ticks)) return "—";

  // .NET ticks (100-nanosecond units) -> Unix milliseconds
  const unixMs = (ticks - TICKS_UNIX_EPOCH_OFFSET) / 10000;
  const utcDate = dayjs.utc(unixMs);
  if (!utcDate.isValid()) return "—";

  const istDate = utcDate.add(5, "hour").add(30, "minute");
  return istDate.format("DD MMM YYYY, h:mm:ss A");
};
